import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Lesson } from '$/models/Lesson';
import lessonRepository from '@/api/repositories/lessonRepository/LessonRepository.ts';
import { Filters } from '$/repositories/supabaseRepository/SupabaseRepository.ts';
import { filtersToStringArray } from '@/utils/filtersToStringArray.ts';

export const useLessons = (enabled: boolean = true, filters: Filters<Lesson> | undefined = undefined) => {
  const queryKey = ['lessons', ...filtersToStringArray(filters)];

  const queryFn = async () => {
    return getLessons(filters);
  };

  return useQuery({ queryKey, queryFn, enabled, staleTime: 1000 * 60 * 5 });
};

export const useLesson = (id: string | undefined) => {
  return useQuery({
    queryKey: ['lessons', id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await lessonRepository.findOne({ id });
      if (error) throw new Error(error.message);
      return data;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
};

export const useCreateLesson = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<Omit<Lesson, 'id' | 'created_at' | 'updated_at'>>) => {
      const { data, error } = await lessonRepository.create(payload);
      if (error) throw new Error(error.message);
      return data!;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['lessons'] });
    },
  });
};

export const useUpdateLesson = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: Partial<Omit<Lesson, 'created_at' | 'updated_at'>> & { id: string }) => {
      const { data, error } = await lessonRepository.update(payload, { id });
      if (error) throw new Error(error.message);
      return data!;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['lessons'] });
      void queryClient.invalidateQueries({ queryKey: ['lessons', variables.id] });
    },
  });
};

async function getLessons(filters?: Filters<Lesson>): Promise<Lesson[]> {
  const { data, error } = filters
    ? await lessonRepository.findMany(filters, { order: { by: 'created_at', ascending: false } })
    : await lessonRepository.all({ order: { by: 'created_at', ascending: false } });
  if (error) throw new Error(error.message);

  return data ?? [];
}


